import type { Ref } from 'vue'
import type { TutorTask } from './tutor-tasks/interfaces'

import { useDisciplineTasksStore } from './tasks/store'
import { useDisciplineTasksApi } from '../api/tasks.hook'
import { getTutorTasksFilterParams } from '../lib/tutor-tasks-query'

interface DisciplineTasksRefreshReturn {
  refreshTasks: (created?: TutorTask | null) => Promise<void>
  writeAndRefresh: (loading: Ref<boolean>, write: () => Promise<TutorTask | boolean | null>) => Promise<void>
}

const byDueDate = (a: TutorTask, b: TutorTask): number =>
  (a.due_date ?? '').localeCompare(b.due_date ?? '') || a.id - b.id

export const useDisciplineTasksRefresh = (): DisciplineTasksRefreshReturn => {
  const route = useRoute()
  const store = useDisciplineTasksStore()

  /** The list reads a replica, so a task created a moment ago may be missing from the refetch; on an
   *  unfiltered first page it is put back from the create response, in the list's own order. */
  const refreshTasks = async (created: TutorTask | null = null): Promise<void> => {
    await useDisciplineTasksApi({ disciplineId: route.params.id as string })

    const hasFilters = Object.values(getTutorTasksFilterParams(store.getFilters)).some(Boolean)
    const isMissing = created && !store.getTasks.some(task => task.id === created.id)

    if (isMissing && store.getCurrentPage === 1 && !hasFilters) {
      store.setTasks([...store.getTasks, created].sort(byDueDate))
    }
  }

  /**
   * A task write from a modal: the modal keeps its loader until the table shows the result, then closes. A refused
   * write closes it at once; its error toast is already shown by the write.
   */
  const writeAndRefresh = async (
    loading: Ref<boolean>,
    write: () => Promise<TutorTask | boolean | null>,
  ): Promise<void> => {
    loading.value = true

    try {
      const result = await write()
      if (result) await refreshTasks(result === true ? null : result)
    } finally {
      loading.value = false
      store.closeModal()
    }
  }

  return {
    refreshTasks,
    writeAndRefresh,
  }
}
